import React, { useState } from "react";
import styled from "@emotion/styled";

import {
  Box,
  Button,
  Fade,
  IconButton,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Toolbar,
  Tooltip,
  Typography as MuiTypography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Delete as DeleteIcon,
  Cancel,
  GridView,
  TableView,
} from "@mui/icons-material";

import { SpacingProps, spacing } from "@mui/system";

import { Check } from "react-feather";

import { useTranslation } from "react-i18next";
import { GridToolbarExport } from "@mui/x-data-grid";
import ExcelImportPreviewDialog from "./ExcelImportPreviewDialog";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const ToolbarTitle = styled.div`
  min-width: 150px;
`;

const Spacer = styled.div`
  flex: 1 1 100%;
`;

interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

type EnhancedDataGridToolbarProps = {
  rowSelectionModel?: any;
  handleDeleteClick?: any;
  view: "cards" | "table";
  setView?: (view: "cards" | "table") => void;
  title: string;
  deleting?: boolean;
  isLoading: boolean;
  customActions?: React.ReactNode[];
  onImport?: (selectedData: any[]) => Promise<void>;
  existingItems?: (string | undefined)[];
};

const EnhancedDataGridToolbar = (props: EnhancedDataGridToolbarProps) => {
  const { t } = useTranslation();

  const { rowSelectionModel } = props;
  const numSelected = rowSelectionModel?.length;

  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleConfirmDelete = async () => {
    // Call the delete function to delete the item
    await props.handleDeleteClick();

    // Hide the confirmation
    setShowConfirmation(false);
  };

  const handleCancelDelete = () => {
    // Hide the confirmation
    setShowConfirmation(false);
  };

  return (
    <Toolbar>
      <ToolbarTitle style={{ width: "fit-content" }}>
        {numSelected > 0 ? (
          <Typography color="inherit" variant="body1" noWrap>
            {t("selected", {
              count: numSelected,
            })}
          </Typography>
        ) : (
          <Typography variant="body1" id="tableTitle" noWrap overflow="visible">
            {props.title}
          </Typography>
        )}
      </ToolbarTitle>

      <Grid container justifyContent="space-between">
        <Grid>{props.customActions?.map((action) => action)}</Grid>

        <Grid>
          <Spacer />
        </Grid>

        <Grid>
          <Grid container>
            <Grid>
              {numSelected > 0 && (
                <>
                  {showConfirmation ? (
                    <Fade in={showConfirmation}>
                      <Box>
                        <Typography component="span" variant="body1">
                          {t("Are you sure?")}
                        </Typography>
                        <IconButton
                          aria-label="Cancel"
                          size="small"
                          onClick={handleCancelDelete}
                        >
                          <Cancel />
                        </IconButton>
                        <IconButton
                          aria-label="Confirm"
                          size="small"
                          onClick={handleConfirmDelete}
                        >
                          <Check />
                        </IconButton>
                      </Box>
                    </Fade>
                  ) : (
                    props.handleDeleteClick && (
                      <Tooltip title="Delete">
                        <Button
                          aria-label="Delete"
                          size="medium"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            setShowConfirmation(true);
                          }}
                          disabled={props.deleting || props.isLoading}
                        >
                          {t("DeleteSingular")} {numSelected}{" "}
                          {numSelected === 1 ? t("item") : t("items")}
                        </Button>
                      </Tooltip>
                    )
                  )}
                </>
              )}
            </Grid>

            {props.setView && (
              <Grid>
                <Tooltip title={t("Switch to card view")}>
                  <IconButton
                    onClick={() => {
                      if (props.setView) {
                        props.setView("cards");
                      }
                    }}
                    color={props.view === "cards" ? "primary" : "default"}
                  >
                    <GridView />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t("Switch to table view")}>
                  <IconButton
                    onClick={() => {
                      if (props.setView) {
                        props.setView("table");
                      }
                    }}
                    color={props.view === "table" ? "primary" : "default"}
                  >
                    <TableView />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}

            {props.onImport && (
              <ExcelImportPreviewDialog
                onImport={props.onImport}
                existingItems={props.existingItems}
              />
            )}
          </Grid>
        </Grid>
      </Grid>

      <GridToolbarExport
        slotProps={{
          button: {
            children: t("Export"),
          },
          tooltip: {
            title: t("Export"),
          },
        }}
      />
    </Toolbar>
  );
};

export default EnhancedDataGridToolbar;
